// teamComparison.js - Team comparison logic helper
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { calculateTeamScores } from './dashboardCalculations';
import { validateTeamComparisonAccess } from './packageManagement';

/**
 * Get user's teams for comparison (security filtered)
 * @param {string} userEmail - Current user's email
 * @param {string} organizationId - User's organization ID
 * @returns {Promise<Array>} Array of teams owned by user
 */
export const getUserTeamsForComparison = async (userEmail, organizationId) => {
  try {
    console.log('🔍 Loading teams for comparison:', { userEmail, organizationId });
    
    const teamsQuery = query(
      collection(db, 'teams'),
      where('createdByEmail', '==', userEmail),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    
    const teamsSnapshot = await getDocs(teamsQuery);
    const teams = [];
    
    for (const teamDoc of teamsSnapshot.docs) {
      const teamData = {
        id: teamDoc.id,
        ...teamDoc.data()
      };
      
      // Load responses for each team to check if it has data
      const responsesQuery = query(
        collection(db, 'responses'),
        where('teamId', '==', teamDoc.id)
      );
      const responsesSnapshot = await getDocs(responsesQuery);
      const responses = responsesSnapshot.docs.map(doc => doc.data());
      
      // Only include teams with responses
      if (responses.length > 0) {
        const teamScores = calculateTeamScores(responses, ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten']);
        
        teams.push({
          ...teamData,
          responseCount: responses.length,
          teamScores,
          responses,
          canCompare: true
        });
      }
    }
    
    console.log('✅ Teams loaded for comparison:', teams.length);
    return teams;
    
  } catch (error) {
    console.error('❌ Error loading teams for comparison:', error);
    throw new Error('Fout bij laden relaties: ' + error.message);
  }
};

/**
 * Compare scores between two teams
 * @param {Object} team1Data - First team data with scores
 * @param {Object} team2Data - Second team data with scores  
 * @returns {Object} Comparison analysis
 */
export const compareTeamScores = (team1Data, team2Data) => {
  try {
    console.log('📊 Comparing team scores:', {
      team1: team1Data.name,
      team2: team2Data.name
    });
    
    const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
    const tredeNames = {
      vertrouwen: 'Vertrouwen',
      conflicten: 'Conflicten',
      betrokkenheid: 'Betrokkenheid', 
      verantwoording: 'Verantwoording',
      resultaten: 'Resultaten'
    };
    
    const comparison = {
      team1: {
        id: team1Data.id,
        name: team1Data.name,
        scores: team1Data.teamScores,
        responseCount: team1Data.responseCount,
        period: team1Data.measurementPeriod || 'Onbekend'
      },
      team2: {
        id: team2Data.id,
        name: team2Data.name,
        scores: team2Data.teamScores,
        responseCount: team2Data.responseCount,
        period: team2Data.measurementPeriod || 'Onbekend'
      },
      tredeComparisons: {},
      insights: [],
      recommendations: [],
      overallAnalysis: {}
    };
    
    // Calculate per-trede comparisons
    let totalDifference = 0;
    let improvementsCount = 0;
    let declinesCount = 0;
    
    tredes.forEach(trede => {
      const score1 = team1Data.teamScores[trede] || 0;
      const score2 = team2Data.teamScores[trede] || 0;
      const difference = score2 - score1; // Omgedraaid, zodat team1 baseline is
      const percentageChange = score2 !== 0 ? ((difference / score2) * 100) : 0;
      
      const tredeComparison = {
        trede,
        name: tredeNames[trede],
        team1Score: score1,
        team2Score: score2,
        difference: Math.round(difference * 100) / 100,
        percentageChange: Math.round(percentageChange * 10) / 10,
        trend: difference > 0.1 ? 'improvement' : difference < -0.1 ? 'decline' : 'stable',
        significance: Math.abs(difference) > 0.5 ? 'significant' : Math.abs(difference) > 0.2 ? 'moderate' : 'minor'
      };
      
      comparison.tredeComparisons[trede] = tredeComparison;
      totalDifference += Math.abs(difference);
      
      if (difference > 0.1) improvementsCount++;
      if (difference < -0.1) declinesCount++;
    });
    
    // Overall analysis
    comparison.overallAnalysis = {
      averageDifference: Math.round((totalDifference / tredes.length) * 100) / 100,
      improvementsCount,
      declinesCount,
      stableCount: tredes.length - improvementsCount - declinesCount,
      overallTrend: improvementsCount > declinesCount ? 'improving' : 
                   declinesCount > improvementsCount ? 'declining' : 'stable'
    };
    
    // Generate insights
    comparison.insights = generateComparisonInsights(comparison);
    
    // Generate recommendations  
    comparison.recommendations = [] // Uitgeschakelde functie voor RelatieScan generateLencioniRecommendations(comparison);
    
    console.log('✅ Team comparison completed:', {
      totalDifference: comparison.overallAnalysis.averageDifference,
      trend: comparison.overallAnalysis.overallTrend
    });
    
    return comparison;
    
  } catch (error) {
    console.error('❌ Error comparing team scores:', error);
    throw new Error('Fout bij vergelijken relaties: ' + error.message);
  }
};

/**
 * Validate that user can compare these teams (security check)
 * @param {string} team1Id - First team ID
 * @param {string} team2Id - Second team ID  
 * @param {string} userEmail - User email
 * @returns {Promise<Object>} Validation result
 */
export const validateTeamComparison = async (team1Id, team2Id, userEmail) => {
  try {
    console.log('🔒 Validating team comparison access:', {
      team1Id,
      team2Id,
      userEmail
    });
    
    // Use existing package validation helper
    const validation = await validateTeamComparisonAccess(userEmail, [team1Id, team2Id]);
    
    if (!validation.hasAccess) {
      return {
        isValid: false,
        message: validation.reason,
        allowedTeams: validation.allowedTeams
      };
    }
    
    // Additional validation: ensure both teams are in allowed list
    const allowedTeamIds = validation.allowedTeams;
    
    if (!allowedTeamIds.includes(team1Id) || !allowedTeamIds.includes(team2Id)) {
      return {
        isValid: false,
        message: 'Een of beide relaties behoren niet tot jouw relaties',
        allowedTeams: allowedTeamIds
      };
    }
    
    console.log('✅ Team comparison validation passed');
    
    return {
      isValid: true,
      message: 'Vergelijking toegestaan',
      allowedTeams: allowedTeamIds,
      packageInfo: validation.packageInfo
    };
    
  } catch (error) {
    console.error('❌ Error validating team comparison:', error);
    return {
      isValid: false,
      message: 'Fout bij validatie: ' + error.message,
      allowedTeams: []
    };
  }
};

/**
 * Generate comparison insights based on analysis
 * @param {Object} comparisonData - Comparison data
 * @returns {Array} Array of insight objects
 */
export const generateComparisonInsights = (comparisonData) => {
  const insights = [];
  const { tredeComparisons, overallAnalysis } = comparisonData;
  
  // Find strongest improvement
  const improvements = Object.values(tredeComparisons)
    .filter(comp => comp.trend === 'improvement')
    .sort((a, b) => b.difference - a.difference);
    
  if (improvements.length > 0) {
    const strongest = improvements[0];
    insights.push({
      type: 'improvement',
      category: 'strength',
      trede: strongest.trede,
      message: `Grootste verbetering in ${strongest.name}: +${strongest.difference} punten (${strongest.percentageChange > 0 ? '+' : ''}${strongest.percentageChange}%)`,
      significance: strongest.significance
    });
  }
  
  // Find biggest decline
  const declines = Object.values(tredeComparisons)
    .filter(comp => comp.trend === 'decline')
    .sort((a, b) => a.difference - b.difference);
    
  if (declines.length > 0) {
    const biggest = declines[0];
    insights.push({
      type: 'decline',
      category: 'concern',
      trede: biggest.trede,
      message: `Grootste achteruitgang in ${biggest.name}: ${biggest.difference} punten (${biggest.percentageChange}%)`,
      significance: biggest.significance
    });
  }
  
  // Overall trend insight
  if (overallAnalysis.overallTrend === 'improving') {
    insights.push({
      type: 'overall',
      category: 'positive',
      message: `Relatie toont algemene verbetering in ${overallAnalysis.improvementsCount} van de 5 Lencioni tredes`,
      significance: overallAnalysis.improvementsCount >= 3 ? 'significant' : 'moderate'
    });
  } else if (overallAnalysis.overallTrend === 'declining') {
    insights.push({
      type: 'overall', 
      category: 'concern',
      message: `Relatie toont achteruitgang in ${overallAnalysis.declinesCount} van de 5 Lencioni tredes`,
      significance: overallAnalysis.declinesCount >= 3 ? 'significant' : 'moderate'
    });
  }
  
  return insights;
};

/**
 * Generate Lencioni-specific recommendations based on comparison
 * Focus on Team2 (current situation) scores and provide actionable advice
 * @param {Object} comparisonData - Comparison data
 * @returns {Array} Array of recommendation objects
 */
export const generateLencioniRecommendations = (comparisonData) => {
  const recommendations = [];
  const { tredeComparisons } = comparisonData;
  
  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
  const tredeNames = {
    vertrouwen: 'Vertrouwen',
    conflicten: 'Conflicten',
    betrokkenheid: 'Betrokkenheid', 
    verantwoording: 'Verantwoording',
    resultaten: 'Resultaten'
  };
  
  // Track problematic scores and all Team2 scores for fallback
  const problematicTredes = [];
  const allTeam2Scores = [];
  
  // 1. PRIMARY CHECK: Team2 scores < 3.5 (Critical/High priority issues)
  tredes.forEach(trede => {
    const comp = tredeComparisons[trede];
    if (comp && comp.team2Score < 3.5) {
      problematicTredes.push({
        trede,
        name: comp.name,
        score: comp.team2Score,
        trend: comp.trend,
        priority: comp.team2Score < 3.0 ? 'critical' : 'high'
      });
    }
    
    // Collect all Team2 scores for fallback analysis
    if (comp) {
      allTeam2Scores.push({
        trede,
        name: comp.name,
        score: comp.team2Score,
        trend: comp.trend
      });
    }
  });
  
  // Generate recommendations for problematic scores
  problematicTredes.forEach(item => {
    const recommendation = generateTredeRecommendation(item.trede, item.name, item.score, item.trend, item.priority);
    if (recommendation) {
      recommendations.push(recommendation);
    }
  });
  
  // 2. SECONDARY CHECK: If all scores >= 3.5, identify lowest for improvement opportunity
  if (problematicTredes.length === 0 && allTeam2Scores.length > 0) {
    // Find lowest Team2 score
    const lowestTrede = allTeam2Scores.reduce((lowest, current) => 
      current.score < lowest.score ? current : lowest
    );
    
    recommendations.push({
      priority: 'medium',
      trede: lowestTrede.trede,
      title: `Verbeterkans: ${lowestTrede.name}`,
      message: `Alle tredes scoren goed (≥3.5), maar ${lowestTrede.name} (${lowestTrede.score.toFixed(1)}) biedt nog ruimte voor verdere verbetering.`,
      actions: generateImprovementActions(lowestTrede.trede)
    });
  }
  
  // 3. SPECIAL CASE: Multiple critical issues require urgent intervention
  const criticalIssues = problematicTredes.filter(item => item.priority === 'critical');
  if (criticalIssues.length >= 2) {
    recommendations.unshift({
      priority: 'critical',
      trede: 'overall',
      title: 'Urgente Team Interventie Vereist',
      message: `Meerdere tredes (${criticalIssues.map(i => i.name).join(', ')}) scoren kritiek laag (<3.0). Dit vereist onmiddellijke, systematische aanpak.`,
      //actions: [
     //   'Plan een urgente team-offsite voor grondige evaluatie',
     //   'Overweeg externe teamcoaching of facilitatie',
     //   'Start met intensieve Lencioni teamassessment workshops',
     //   'Stel een concreet verbeterplan op met meetbare doelen'
    //  ]
    });
  }
  
  return recommendations;
};

/**
 * Generate specific recommendation for a Lencioni trede
 * @param {string} trede - Trede identifier
 * @param {string} name - Display name
 * @param {number} score - Current Team2 score
 * @param {string} trend - Trend direction
 * @param {string} priority - Priority level
 * @returns {Object} Recommendation object
 */
function generateTredeRecommendation(trede, name, score, trend, priority) {
  const baseMessage = `${name} scoort ${score.toFixed(1)} en vereist directe aandacht.`;
  const trendContext = trend === 'decline' ? ' Er is ook sprake van achteruitgang ten opzichte van de vorige meting.' : '';
  
  const recommendations = {
    vertrouwen: {
      title: 'Versterk de Fundatie: Vertrouwen',
      message: baseMessage + ' Vertrouwen is de basis van de Lencioni piramide.' + trendContext,
       actions: [
      'Organiseer team-building activiteiten gericht op kwetsbaarheid',
      'Stimuleer open communicatie en persoonlijke verhalen delen',
      'Voer individuele 1-op-1 gesprekken met alle teamleden',
      'Creëer een veilige omgeving voor het maken van fouten'
      ]
    },
    conflicten: {
      title: 'Stimuleer Gezonde Conflicten',
      message: baseMessage + ' Het team vermijdt waarschijnlijk productieve conflicten.' + trendContext,
     actions: [
       'Train het team in constructieve discussie- en debattechnieken',
       'Maak expliciet ruimte voor meningsverschillen in meetings',
       'Model zelf het aangaan van productieve conflicten als leider',
       'Introduceer gestructureerde discussieformats (devils advocate, etc.)'
     ]
    },
    betrokkenheid: {
      title: 'Verbeter Betrokkenheid en Commitment',
      message: baseMessage + ' Teamleden zijn mogelijk niet volledig gecommitteerd aan beslissingen.' + trendContext,
      actions: [
       'Zorg voor duidelijke en transparante besluitvorming in meetings',
       'Vraag expliciet om commitment van alle teamleden bij besluiten',
       'Documenteer beslissingen en verwachtingen helder',
     'Implementeer een "disagree but commit" cultuur'
    ]
    },
    verantwoording: {
      title: 'Verhoog Wederzijdse Verantwoording',
      message: baseMessage + ' Teamleden spreken elkaar onvoldoende aan op prestaties.' + trendContext,
     actions: [
       'Stel duidelijke, meetbare doelen en verwachtingen vast',
       'Implementeer regelmatige voortgangsreviews',
       'Train teamleden in het geven van constructieve feedback',
       'Creëer systemen voor peer-to-peer accountability'
     ]
    },
    resultaten: {
      title: 'Focus op Collectieve Resultaten',
      message: baseMessage + ' Het team is mogelijk te gefocust op individuele doelen.' + trendContext,
    actions: [
      'Definieer duidelijke, gezamenlijke teamdoelen',
     'Align individuele KPIs met teamresultaten',
      'Vier teamsuccessen en leer van teamfalen',
       'Implementeer transparante teamscoring en -reporting'
     ]
    }
  };
  
  const recommendation = recommendations[trede];
  if (!recommendation) return null;
  
  return {
    priority,
    trede,
    title: recommendation.title,
    message: recommendation.message,
    actions: recommendation.actions
  };
}

/**
 * Generate improvement actions for tredes that score well (≥3.5)
 * @param {string} trede - Trede identifier
 * @returns {Array} Array of improvement actions
 */
function generateImprovementActions(trede) {
  const improvements = {
    vertrouwen: [
      'Verdiep persoonlijke connecties tussen teamleden',
      'Organiseer informele team-activiteiten',
      'Deel meer persoonlijke verhalen en ervaringen'
    ],
    conflicten: [
      'Introduceer meer geavanceerde discussietechnieken',
      'Experimenteer met nieuwe brainstorm- en ideation methoden',
      'Versterk de debatcultuur met complexere onderwerpen'
    ],
    betrokkenheid: [
      'Vergroot eigenaarschap door meer autonomie te geven',
      'Betrek het team bij strategische planning',
      'Implementeer team-gedreven initiatieven'
    ],
    verantwoording: [
      'Introduceer peer coaching en mentoring',
      'Versterk de feedback cultuur',
      'Implementeer 360-graden feedback systemen'
    ],
    resultaten: [
      'Stel ambitieuzere teamdoelen',
      'Innoveer op het gebied van teamcollaboratie',
      'Deel best practices met andere teams'
    ]
  };
  
  return improvements[trede] || [
    'Blijf de goede prestaties volhouden',
    'Zoek naar verdere innovatie mogelijkheden',
    'Deel kennis en ervaring met andere teams'
  ];
}